// ============================================
// DailyResetOverlay — a full-screen "daily reset" that appears the first time a
// user opens the app after the reset hour (default 4 AM local). It blurs the app
// behind it, shows the day's Bible verse, and a personalized AI briefing that
// recaps what came in yesterday and frames the day ahead — scoped to the user
// and their department(s).
//
// Self-contained and globally mounted once in Layout.tsx (mirrors
// KeyboardShortcutsOverlay). The blurred, immersive visual language mirrors
// FocusMode's BreakScreen.
// ============================================

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sunrise, Sparkles, ArrowRight, Loader2, RefreshCw, AlertCircle, Quote } from 'lucide-react';
import { useAuth } from '../data/AuthContext';
import {
  fetchDailyReset, getDailyResetConfig,
  type DailyReset,
} from '../data/api';

const LS_KEY = 'stageflow:dailyReset:lastShownDay';

// marked + DOMPurify are loaded on demand when a briefing arrives. This
// overlay is the only markdown consumer in the eager import graph, so a
// static import here would drag both libraries into every visitor's
// initial bundle (including the signed-out marketing page).
async function renderMarkdown(source: string): Promise<string> {
  const [{ marked }, { default: DOMPurify }] = await Promise.all([
    import('marked'),
    import('dompurify'),
  ]);
  const raw = marked.parse(source, { async: false, gfm: true, breaks: false }) as string;
  return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
}

// Local reset-day key (YYYY-MM-DD) using the BROWSER's local time with a
// resetHour cutoff, so "4 AM locally" is true per user. Shifting back by
// resetHour hours rolls the date over at that hour instead of midnight.
function localDayKey(resetHour: number): string {
  const shifted = new Date(Date.now() - resetHour * 60 * 60 * 1000);
  const y = shifted.getFullYear();
  const m = String(shifted.getMonth() + 1).padStart(2, '0');
  const d = String(shifted.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Dark-theme prose styling for the rendered Markdown briefing.
const PROSE = [
  'max-w-none text-[15px] leading-relaxed text-indigo-100/90',
  '[&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-[13px] [&_h2]:font-semibold [&_h2]:uppercase [&_h2]:tracking-[1.5px] [&_h2]:text-amber-300/80',
  '[&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:text-[14px] [&_h3]:font-semibold [&_h3]:text-white',
  '[&_p]:my-2.5',
  '[&_ul]:my-2.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1',
  '[&_ol]:my-2.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1',
  '[&_li]:text-indigo-100/85',
  '[&_strong]:font-semibold [&_strong]:text-white',
  '[&_em]:italic [&_em]:text-indigo-200/80',
  '[&_a]:text-indigo-300 [&_a]:underline',
  '[&_hr]:my-5 [&_hr]:border-white/10',
].join(' ');

export function DailyResetOverlay() {
  const { currentUser } = useAuth();

  const [enabled, setEnabled] = useState(true);
  const [resetHour, setResetHour] = useState(4);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DailyReset | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Guards a single fetch attempt per reset-day per session (so window focus
  // events don't re-trigger a fetch once we've already tried today).
  const attemptedDayRef = useRef<string | null>(null);

  // Load config once (defaults are sensible if this fails).
  useEffect(() => {
    let cancelled = false;
    getDailyResetConfig()
      .then((cfg) => {
        if (cancelled) return;
        setEnabled(cfg.enabled !== false);
        setResetHour(typeof cfg.resetHour === 'number' ? cfg.resetHour : 4);
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  const runFetch = useCallback((userId: string) => {
    setLoading(true);
    setError(null);
    fetchDailyReset(userId)
      .then((d) => setData(d))
      .catch((err) => setError(String(err?.message || err)))
      .finally(() => setLoading(false));
  }, []);

  const maybeShow = useCallback(() => {
    if (!currentUser || !enabled || open) return;
    const dayKey = localDayKey(resetHour);
    let lastShown: string | null = null;
    try { lastShown = localStorage.getItem(LS_KEY); } catch { /* ignore */ }
    if (lastShown === dayKey) return;
    if (attemptedDayRef.current === dayKey) return; // already tried this session
    attemptedDayRef.current = dayKey;
    setData(null);
    setOpen(true);
    runFetch(currentUser.id);
  }, [currentUser, enabled, resetHour, open, runFetch]);

  // Evaluate on mount, and whenever the tab regains focus/visibility (covers the
  // app being left open overnight past the reset hour).
  useEffect(() => {
    maybeShow();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeShow();
    };
    window.addEventListener('focus', maybeShow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', maybeShow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [maybeShow]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(LS_KEY, localDayKey(resetHour)); } catch { /* ignore */ }
    setOpen(false);
  }, [resetHour]);

  const [summaryHtml, setSummaryHtml] = useState('');
  useEffect(() => {
    const summary = data?.summary;
    if (!summary) {
      setSummaryHtml('');
      return;
    }
    let cancelled = false;
    renderMarkdown(summary)
      .then((html) => { if (!cancelled) setSummaryHtml(html); })
      .catch(() => { /* briefing stays blank; verse and stats still render */ });
    return () => { cancelled = true; };
  }, [data?.summary]);

  const dateStr = useMemo(
    () => new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
    [open],
  );

  if (!currentUser) return null;

  const stats = data?.stats;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-950/80 backdrop-blur-xl"
          role="dialog"
          aria-modal="true"
          aria-label="Daily reset"
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="my-auto w-full max-w-2xl px-6 py-14"
          >
            {/* Header */}
            <div className="flex flex-col items-center text-center mb-8">
              <div className="flex items-center gap-2 text-amber-300/80">
                <Sunrise className="w-5 h-5" />
                <span className="text-[12px] font-semibold uppercase tracking-[2px]">Daily Reset</span>
              </div>
              <p className="mt-2 text-[13px] text-indigo-200/60">{dateStr}</p>
            </div>

            {/* Verse — the hero */}
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] px-7 py-8 text-center shadow-2xl">
              <Quote className="mx-auto mb-4 h-6 w-6 text-indigo-300/40" />
              {data?.verse ? (
                <>
                  <p className="text-[20px] leading-relaxed font-light text-white/95">
                    “{data.verse.text}”
                  </p>
                  <p className="mt-4 text-[13px] font-semibold uppercase tracking-[1.5px] text-amber-300/80">
                    {data.verse.reference}
                    {data.verse.version ? <span className="text-indigo-200/40"> · {data.verse.version}</span> : null}
                  </p>
                </>
              ) : error ? (
                <div className="flex items-center justify-center gap-2 py-4 text-rose-300/80">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span className="text-[13px]">Couldn’t load today’s verse.</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 py-4 text-indigo-200/50">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-[13px]">Loading today’s verse…</span>
                </div>
              )}
            </div>

            {/* Personalized briefing */}
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] px-7 py-6">
              <div className="mb-3 flex items-center gap-2 text-indigo-200/70">
                <Sparkles className="h-4 w-4 text-indigo-300" />
                <span className="text-[12px] font-semibold uppercase tracking-[1.5px]">Your day ahead</span>
              </div>

              {loading && !data && (
                <div className="flex items-center gap-2 py-6 text-indigo-200/60">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-[14px]">Preparing your personalized summary…</span>
                </div>
              )}

              {error && !loading && (
                <div className="py-4">
                  <div className="flex items-start gap-2 text-rose-300/90">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p className="text-[13px]">Couldn’t load your summary just now.</p>
                  </div>
                  <button
                    onClick={() => currentUser && runFetch(currentUser.id)}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-[13px] text-indigo-200/80 transition-colors hover:bg-white/5"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Try again
                  </button>
                </div>
              )}

              {data && !loading && (
                <div className={PROSE} dangerouslySetInnerHTML={{ __html: summaryHtml }} />
              )}

              {/* Stat chips */}
              {stats && !loading && (
                <div className="mt-5 flex flex-wrap gap-2">
                  <StatChip label="Active" value={stats.personalActive} tone="neutral" />
                  {stats.overdue > 0 && <StatChip label="Overdue" value={stats.overdue} tone="danger" />}
                  {stats.dueToday > 0 && <StatChip label="Due today" value={stats.dueToday} tone="warn" />}
                  {stats.inReview > 0 && <StatChip label="In review" value={stats.inReview} tone="info" />}
                </div>
              )}
            </div>

            {/* Dismiss */}
            <div className="mt-8 flex justify-center">
              <button
                onClick={dismiss}
                className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 px-6 py-3 text-[15px] font-semibold text-white shadow-lg shadow-indigo-900/40 transition-transform hover:scale-[1.02] active:scale-95"
              >
                Start my day
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StatChip({ label, value, tone }: { label: string; value: number; tone: 'neutral' | 'danger' | 'warn' | 'info' }) {
  const tones: Record<string, string> = {
    neutral: 'border-white/10 bg-white/5 text-indigo-100/80',
    danger: 'border-rose-400/20 bg-rose-500/10 text-rose-200',
    warn: 'border-amber-400/20 bg-amber-500/10 text-amber-200',
    info: 'border-indigo-400/20 bg-indigo-500/10 text-indigo-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium ${tones[tone]}`}>
      <span className="font-semibold tabular-nums">{value}</span>
      {label}
    </span>
  );
}
